import { Add, Delete } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import Api from "api";
import { useApiClient } from "app/hooks";
import { ErrorContext } from "components/contexts/error-handler";
import { useNavigate } from "react-router-dom";
import { Script } from "generated/client";
import strings from "localization/strings";
import React, { useContext, useEffect, useState } from "react";

/**
 * Scripts screen
 */
const ScriptsScreen: React.FC = () => {
  const [ loading, setLoading ] = useState(false);
  const errorContext = useContext(ErrorContext);
  const [ scripts, setScripts ] = useState<Script[]>([]);
  const [ search, setSearch ] = useState("");
  const navigate = useNavigate();
  const apiClient = useApiClient(Api.getApiClient);
  const { scriptsApi } = apiClient;

  /**
   * Load scripts
   */
  const loadScripts = async () => {
    setLoading(true);

    try {
      setScripts(await scriptsApi.listScripts());
    } catch (e) {
      errorContext.setError(strings.errorHandling.adminFormsScreen.listForms, e);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadScripts();
  }, []);

  /**
   * Delete script
   * 
   * @param id script id
   */
  const deleteScript = async (id: string) => {
    setLoading(true);

    try {
      await scriptsApi.deleteScript({ scriptId: id });
      setScripts(scripts.filter(script => script.id !== id));
    } catch (e) {
      errorContext.setError(strings.errorHandling.scriptEditorScreen.findScript, e);
    }

    setLoading(false);
  };

  const filteredScripts = scripts.filter(script => script.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()));

  return (
    <Stack flex={ 1 } minWidth={ 0 }>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={ 1 }
        useFlexGap
        flexWrap="wrap"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={ 2.25 }
      >
        <TextField
          value={ search }
          onChange={ event => setSearch(event.target.value) }
          placeholder={ strings.scriptsScreen.search }
          sx={{
            flex: { md: "1 1 300px" },
            minWidth: { md: 300 },
            maxWidth: { md: 340 },
            "& .MuiInputBase-root": {
              backgroundColor: "#fff",
              color: "#1a1a1a",
              fontSize: 14,
              lineHeight: "20px"
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#6e7472",
              fontSize: 14,
              opacity: 1
            }
          }}
        />
        <Button
          variant="contained"
          endIcon={ <Add/> }
          onClick={ () => navigate("newScript") }
          sx={{
            ml: { md: "auto !important" },
            alignSelf: { xs: "stretch", md: "auto" },
            backgroundColor: "#ED8113",
            borderRadius: 1,
            color: "#fff",
            fontSize: 14,
            fontWeight: 500,
            px: 2.25,
            py: 0.875,
            "&:hover": { backgroundColor: "#c86b0e" }
          }}
        >
          { strings.navigationHeader.scriptsScreens.newScriptButton }
        </Button>
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          borderColor: "#e2e5e3",
          borderRadius: 1,
          overflow: "hidden"
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 80px",
              gap: 2,
              px: 2.25,
              py: 1.25,
              backgroundColor: "#fafbfa",
              borderBottom: "1px solid #e2e5e3",
              color: "#6e7472",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.7px",
              textTransform: "uppercase"
            }}
          >
            <Box>{ strings.scriptsScreen.scriptsTable.script }</Box>
            <Box textAlign="right">{ strings.scriptsScreen.scriptsTable.delete }</Box>
          </Box>
          { loading && <Box display="flex" justifyContent="center" p={ 4 }><CircularProgress size={ 28 }/></Box> }
          { !loading && !filteredScripts.length &&
            <Typography sx={{
              color: "#8a8f8c",
              fontSize: 13,
              p: 2.25
            }}
            >
              { strings.scriptsScreen.noScripts }
            </Typography>
          }
          { !loading && filteredScripts.map(script => (
            <Box
              key={ script.id }
              onClick={ () => script.id && navigate(`${script.id}/`) }
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) 80px",
                gap: 2,
                alignItems: "center",
                cursor: "pointer",
                px: 2.25,
                py: 1.25,
                borderTop: "1px solid #f0f2f1",
                "&:hover": { backgroundColor: "#f8faf8" }
              }}
            >
              <Typography sx={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px"
              }}
              >
                { script.name }
              </Typography>
              <Box textAlign="right">
                <Tooltip title={ strings.scriptsScreen.scriptsTable.delete }>
                  <IconButton
                    aria-label={ strings.scriptsScreen.scriptsTable.delete }
                    size="small"
                    onClick={ event => {
                      event.stopPropagation();
                      if (script.id) deleteScript(script.id);
                    } }
                    sx={{ color: "#bf3f32" }}
                  >
                    <Delete fontSize="small"/>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )) }
        </Box>
      </Paper>
    </Stack>
  );
};

export default ScriptsScreen;